/**
 * Gift Card Manager for WooCommerce Blocks
 *
 * This provides full gift card functionality for the block-based checkout,
 * replicating the behavior of the PHP GiftCardManager and gift-cards.js
 */

// biome-ignore lint/style/useImportType: Err this is not true
import React from "react";
import { useState, useEffect } from "react";
import { registerPlugin } from "@wordpress/plugins";
import { __ } from "@wordpress/i18n";
import { getTranslatedText, replaceStrings } from "~/shared/utils/i18n";
import "./giftcard-balance-checker.scss";

// Types
interface GiftCard {
	code: string;
	uuid: string;
	amount: number;
	balance: number;
	amount_formatted: string;
	balance_formatted: string;
}

interface CartTotals {
	subtotal: number;
	total: string;
	total_discount: number;
	total_tax: number;
	total_shipping: number;
}

interface GiftCardResponse {
	success: boolean;
	message?: string;
	card?: GiftCard;
	cards?: GiftCard[];
	totals?: CartTotals;
}

interface GiftCardManagerProps {
	cart?: {
		cartItems?: unknown[];
		cartTotals?: CartTotals;
	};
	extensions?: Record<string, unknown>;
	context?: string;
}

// API Service
class GiftCardAPI {
	private baseUrl = "/wp-json/leat/v1/giftcards";
	private nonce: string;

	constructor() {
		// Get nonce from window or use the one from config
		this.nonce =
			window.wp?.apiFetch?.nonceMiddleware?.nonce ||
			window.leatGiftCardConfig?.nonce ||
			"";
	}

	async checkBalance(code: string): Promise<GiftCardResponse> {
		try {
			const response = await fetch(`${this.baseUrl}/check`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": this.nonce,
				},
				body: JSON.stringify({ code }),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || "Failed to check balance");
			}

			return data;
		} catch (error) {
			console.error("Error checking gift card balance:", error);
			throw error;
		}
	}

	async addToSession(code: string): Promise<GiftCardResponse> {
		try {
			const response = await fetch(`${this.baseUrl}/session/add`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": this.nonce,
				},
				body: JSON.stringify({ code }),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || "Failed to add gift card");
			}

			return data;
		} catch (error) {
			console.error("Error adding gift card:", error);
			throw error;
		}
	}

	async removeFromSession(code: string): Promise<GiftCardResponse> {
		try {
			const response = await fetch(`${this.baseUrl}/session/remove`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": this.nonce,
				},
				body: JSON.stringify({ code }),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || "Failed to remove gift card");
			}

			return data;
		} catch (error) {
			console.error("Error removing gift card:", error);
			throw error;
		}
	}

	async getAppliedCards(): Promise<GiftCardResponse> {
		try {
			const response = await fetch(`${this.baseUrl}/session`, {
				method: "GET",
				headers: {
					"X-WP-Nonce": this.nonce,
				},
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || "Failed to get applied cards");
			}

			return data;
		} catch (error) {
			console.error("Error getting applied gift cards:", error);
			throw error;
		}
	}
}

// Gift Card Form Component
const GiftCardForm: React.FC<{
	onAdd: (code: string) => Promise<void>;
	isLoading: boolean;
}> = ({ onAdd, isLoading }) => {
	const [code, setCode] = useState("");
	const [isApplying, setIsApplying] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!code.trim() || isApplying) return;

		setIsApplying(true);
		try {
			await onAdd(code.trim());
			setCode("");
		} finally {
			setIsApplying(false);
		}
	};

	const config = window.leatGiftCardConfig || {};

	return (
		<form onSubmit={handleSubmit} className="leat-giftcard-form">
			<div className="leat-giftcard-input-group">
				<input
					type="text"
					value={code}
					onChange={(e) => setCode(e.target.value)}
					placeholder={
						getTranslatedText(config.inputPlaceholder || null) ||
						__("Enter gift card code", "leat-crm")
					}
					disabled={isApplying || isLoading}
					className="leat-giftcard-input"
				/>
				<button
					type="submit"
					disabled={!code.trim() || isApplying || isLoading}
					className="leat-giftcard-apply-btn wp-element-button"
				>
					{isApplying
						? getTranslatedText(config.checkingText) ||
							__("Checking...", "leat-crm")
						: getTranslatedText(config.applyButtonText || null) ||
							__("Apply", "leat-crm")}
				</button>
			</div>
		</form>
	);
};

// Applied Gift Cards List Component
const AppliedGiftCards: React.FC<{
	cards: GiftCard[];
	onRemove: (code: string) => Promise<void>;
}> = ({ cards, onRemove }) => {
	const [removingCard, setRemovingCard] = useState<string | null>(null);

	if (cards.length === 0) {
		return null;
	}

	const handleRemove = async (code: string) => {
		setRemovingCard(code);
		try {
			await onRemove(code);
		} finally {
			setRemovingCard(null);
		}
	};

	const config = window.leatGiftCardConfig || {};

	return (
		<div className="leat-applied-giftcards">
			<h4>
				{getTranslatedText(config.appliedSectionTitle || null) ||
					__("Applied Gift Cards", "leat-crm")}
			</h4>
			<ul className="leat-giftcard-list">
				{cards.map((card) => (
					<li key={card.code} className="leat-giftcard-item">
						<span className="leat-giftcard-code">{card.code}</span>
						<span
							className="leat-giftcard-amount"
							dangerouslySetInnerHTML={{
								__html: `-${card.amount_formatted}`,
							}}
						/>
						<span
							className="leat-giftcard-balance"
							dangerouslySetInnerHTML={{
								__html: replaceStrings(
									getTranslatedText(config.balanceText || null) ||
										"Balance: {{balance}}",
									[{ "{{balance}}": card.balance_formatted }],
								),
							}}
						/>
						<button
							type="button"
							onClick={() => handleRemove(card.code)}
							disabled={removingCard === card.code}
							className="leat-giftcard-remove"
							aria-label={__("Remove gift card", "leat-crm")}
						>
							×
						</button>
					</li>
				))}
			</ul>
		</div>
	);
};

// Main Gift Card Manager Component
export const GiftCardManager: React.FC<GiftCardManagerProps> = () => {
	const [appliedCards, setAppliedCards] = useState<GiftCard[]>([]);
	const [message, setMessage] = useState<{
		type: "success" | "error";
		text: string;
	} | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const api = new GiftCardAPI();
	const config = window.leatGiftCardConfig || {};

	// Load applied cards on mount
	useEffect(() => {
		loadAppliedCards();
	}, []);

	// Listen for checkout updates
	useEffect(() => {
		const handleUpdate = () => {
			loadAppliedCards();
		};

		document.addEventListener("wc-blocks-checkout-update", handleUpdate);
		return () => {
			document.removeEventListener("wc-blocks-checkout-update", handleUpdate);
		};
	}, []);

	// Auto-hide messages after 5 seconds (matching jQuery behavior)
	useEffect(() => {
		if (message) {
			// Set timeout to hide message after 5 seconds
			const timeoutId = setTimeout(() => {
				setMessage(null);
			}, 5000);

			// Cleanup timeout on unmount or message change
			return () => {
				clearTimeout(timeoutId);
			};
		}
	}, [message]);

	const loadAppliedCards = async () => {
		try {
			const response = await api.getAppliedCards();
			if (response.success && response.cards) {
				setAppliedCards(response.cards);
			}
		} catch (error) {
			console.error("Failed to load applied gift cards:", error);
		}
	};

	const handleAddCard = async (code: string) => {
		setMessage(null);
		setIsLoading(true);

		try {
			// First check the balance
			const balanceResponse = await api.checkBalance(code);

			if (balanceResponse.success) {
				// Then add to session
				const addResponse = await api.addToSession(code);

				if (addResponse.success) {
					setMessage({
						type: "success",
						text:
							addResponse.message ||
							getTranslatedText(config.successMessage || null) ||
							__("Gift card applied successfully", "leat-crm"),
					});

					// Reload applied cards
					await loadAppliedCards();

					// Trigger Store API cart update to recalculate totals
					// This invalidates the cart cache and forces a recalculation
					if (window.wc?.blocksCheckout?.extensionCartUpdate) {
						try {
							const cartData =
								await window.wc.blocksCheckout.extensionCartUpdate({
									namespace: "leat-giftcards",
									data: { action: "refresh" },
								});
							console.log("Cart updated successfully:", cartData);
						} catch (updateError) {
							console.error("Failed to update cart totals:", updateError);
							// Fallback: Force a cart data refresh by dispatching an event
							// This triggers the blocks to re-fetch cart data from the API
							window.dispatchEvent(new CustomEvent("wc-blocks_added_to_cart"));
						}
					} else {
						// Fallback for when extensionCartUpdate is not available
						window.dispatchEvent(new CustomEvent("wc-blocks_added_to_cart"));
					}
				}
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			setMessage({
				type: "error",
				text:
					errorMessage ||
					config.errorText ||
					__("Failed to apply gift card", "leat-crm"),
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveCard = async (code: string) => {
		setMessage(null);

		try {
			const response = await api.removeFromSession(code);

			if (response.success) {
				setMessage({
					type: "success",
					text: response.message || __("Gift card removed", "leat-crm"),
				});

				// Reload applied cards
				await loadAppliedCards();

				// Trigger Store API cart update to recalculate totals
				// This invalidates the cart cache and forces a recalculation
				if (window.wc?.blocksCheckout?.extensionCartUpdate) {
					try {
						const cartData =
							await window.wc.blocksCheckout.extensionCartUpdate({
								namespace: "leat-giftcards",
								data: { action: "refresh" },
							});
						console.log("Cart updated successfully:", cartData);
					} catch (updateError) {
						console.error("Failed to update cart totals:", updateError);
						// Fallback: Force a cart data refresh by dispatching an event
						window.dispatchEvent(new CustomEvent("wc-blocks_added_to_cart"));
					}
				} else {
					// Fallback for when extensionCartUpdate is not available
					window.dispatchEvent(new CustomEvent("wc-blocks_added_to_cart"));
				}
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			setMessage({
				type: "error",
				text: errorMessage || __("Failed to remove gift card", "leat-crm"),
			});
		}
	};

	// Don't show if gift cards are disabled
	const allowAcceptance = config.allowAcceptance !== "off";
	if (!allowAcceptance) {
		return null;
	}

	return (
		<div className="leat-giftcard-manager">
			<h3>
				{getTranslatedText(config.formTitle || null) ||
					__("Gift Cards", "leat-crm")}
			</h3>

			<GiftCardForm onAdd={handleAddCard} isLoading={isLoading} />

			{message && (
				<div id="gift-card-messages" className="leat-giftcard-message">
					<div
						className={
							message.type === "error"
								? "woocommerce-error"
								: "woocommerce-message"
						}
					>
						{message.text}
					</div>
				</div>
			)}

			<AppliedGiftCards cards={appliedCards} onRemove={handleRemoveCard} />
		</div>
	);
};

// Integration with WooCommerce Blocks
export function initGiftCardManager(): void {
	const { ExperimentalDiscountsMeta } = window.wc?.blocksCheckout || {};

	if (!ExperimentalDiscountsMeta) {
		console.error("WooCommerce Blocks checkout components not found");
		return;
	}

	const render = () => {
		return (
			<ExperimentalDiscountsMeta>
				<GiftCardManager />
			</ExperimentalDiscountsMeta>
		);
	};

	registerPlugin("leat-giftcard-manager", {
		render,
		scope: "woocommerce-checkout",
	});
}

export default GiftCardManager;
